'use client';

import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'src/redux';
import {
  setSelectedFilterSet as setSelectedFilterSetAction,
  setSelectedCohort as setSelectedCohortAction,
  clearSelectedProfile as clearSelectedProfileAction,
  setAllCodeSets as setAllCodeSetsAction,
  clearAllSelections,
} from 'src/redux/slices/profileSelectionsSlice';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { slugToProfileType } from 'src/utils/profileUrl';

const useProfileSelections = (location: string) => {
  const widgetConfig = useWidgetConfig();
  const dispatch = useAppDispatch();
  const appliedSelections = useAppSelector(
    (state) => state.profileSelections.appliedSelections
  );
  const isHydrated = useAppSelector(
    (state) => state.profileSelections.isHydrated
  );

  // Normalize URL slugs (e.g. 'site-of-care') to the slice's internal key
  // (e.g. 'site_of_care') so reads/writes match. No-op for unmapped keys. TRI-1703.
  const normalizedLocation = slugToProfileType(location);

  // Get the selected profile for this specific location
  const locationSelection =
    appliedSelections[normalizedLocation as keyof typeof appliedSelections] ||
    null;

  // When inside a WidgetConfigProvider (V2 profile layout), the widget's
  // filter set (derived from its code_set) is the source of truth.
  // When outside a WidgetConfigProvider (e.g. ProfileFilterBar on overview pages),
  // fall back to the Redux-managed filter set so the dropdown still works.
  const isInWidgetContext = !!widgetConfig;

  const selectedFilterSet = isInWidgetContext
    ? widgetConfig.filterSet ?? null
    : locationSelection?.filter_set ?? null;
  const selectedCohort = locationSelection?.cohort || null;
  const isAllCodeSets = isInWidgetContext
    ? false
    : (locationSelection?.isAllCodeSets || false);

  const setSelectedFilterSet = useCallback(
    (item: MedScout.FilterSet | null) => {
      dispatch(setSelectedFilterSetAction({ location: normalizedLocation, item }));
    },
    [dispatch, normalizedLocation]
  );

  const setSelectedCohort = useCallback(
    (item: MedScout.Cohort | null) => {
      dispatch(setSelectedCohortAction({ location: normalizedLocation, item }));
    },
    [dispatch, normalizedLocation]
  );

  const clearSelectedProfile = useCallback(() => {
    dispatch(clearSelectedProfileAction({ location: normalizedLocation }));
  }, [dispatch, normalizedLocation]);

  const setAllCodeSets = useCallback(() => {
    dispatch(setAllCodeSetsAction({ location: normalizedLocation }));
  }, [dispatch, normalizedLocation]);

  const clearAllProfileSelections = useCallback(() => {
    dispatch(clearAllSelections());
  }, [dispatch]);

  // Helper to check if this location has a selection
  const hasSelection = useCallback(() => {
    return selectedFilterSet !== null || selectedCohort !== null;
  }, [selectedFilterSet, selectedCohort]);

  // Helper to check if this location has a filter set selection
  const hasFilterSetSelection = useCallback(() => {
    return selectedFilterSet !== null;
  }, [selectedFilterSet]);

  // Helper to check if this location has a cohort selection
  const hasCohortSelection = useCallback(() => {
    return selectedCohort !== null;
  }, [selectedCohort]);

  // Helper to get selection type
  const selectionType = selectedFilterSet
    ? 'filter_set'
    : selectedCohort
    ? 'cohort'
    : null;

  // Helper to check if any location has a selection
  const hasAnySelection = useCallback(() => {
    return Object.values(appliedSelections).some(
      (selection) =>
        selection !== null &&
        (selection.filter_set !== null || selection.cohort !== null)
    );
  }, [appliedSelections]);

  // Helper to get all selected items across locations
  const getAllSelectedProfiles = useCallback(() => {
    return Object.entries(appliedSelections)
      .filter(([, selection]) => selection !== null)
      .reduce((acc, [location, selection]) => {
        if (selection?.filter_set) {
          acc[location] = { type: 'filter_set', item: selection.filter_set };
        } else if (selection?.cohort) {
          acc[location] = { type: 'cohort', item: selection.cohort };
        }
        return acc;
      }, {} as Record<string, { type: 'filter_set' | 'cohort'; item: MedScout.FilterSet | MedScout.Cohort }>);
  }, [appliedSelections]);

  return {
    // Location-specific state
    selectedFilterSet,
    selectedCohort,
    selectionType,
    isHydrated,
    isAllCodeSets,

    // Location-specific methods
    setSelectedFilterSet,
    setSelectedCohort,
    clearSelectedProfile,
    setAllCodeSets,
    hasSelection,
    hasFilterSetSelection,
    hasCohortSelection,

    // Global methods
    clearAllProfileSelections,
    hasAnySelection,
    getAllSelectedProfiles,
  };
};

export default useProfileSelections;
